import React, { useState, useEffect } from 'react';
import { showAppAlert } from '../utils/alertService';
import { EMAIL_INVALID_MESSAGE, isValidKnownProviderEmail, normalizeEmail } from '../utils/emailValidation';
import LoaderOverlay from './LoaderOverlay';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function AddLojistaModal({ isOpen, onClose, onLojistaAdded }) {
  const [formData, setFormData] = useState({
    nome: '',
    nomeFantasia: '',
    email: '',
    senha: '',
    celular: '',
    urlLogotipo: '',
    valor_ponto: '',
    dias_ausencia: '',
    plano_id: '',
    placeid: '',
  });
  
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      fetch(`${API_URL}/plans/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => setPlanos(Array.isArray(data) ? data : []))
        .catch(() => setPlanos([]));
    }
  }, [isOpen, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isValidKnownProviderEmail(formData.email)) {
      showAppAlert('error', EMAIL_INVALID_MESSAGE);
      setLoading(false);
      return;
    }

    const payload = {
      nome: formData.nome,
      nomeFantasia: formData.nomeFantasia,
      email: normalizeEmail(formData.email),
      senha: formData.senha,
      celular: (formData.celular || '').replace(/\D/g, ''),
      urlLogotipo: validateUrl(formData.urlLogotipo) ? formData.urlLogotipo : null,
      valor_ponto: parseFloat(formData.valor_ponto),
      dias_ausencia: parseInt(formData.dias_ausencia, 10) || 0,
      plano_id: Number(formData.plano_id),
      placeid: formData.placeid,
    };

    try {
      const response = await fetch(`${API_URL}/admin/lojistas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const newLojista = await response.json();
        showAppAlert('success', 'Lojista cadastrado com sucesso!');
        onLojistaAdded(newLojista);
        onClose();
      } else {
        const errorData = await response.json();
        let errorMessage = errorData.message || 'Erro ao cadastrar o lojista. Tente novamente.';
        if (errorData.errors) {
            const errorMessages = Object.values(errorData.errors).flat();
            errorMessage = errorMessages.join(' ');
        }
        showAppAlert('error', errorMessage);
      }
    } catch (error) {
      console.error('Erro ao enviar os dados:', error);
      showAppAlert('error', 'Erro ao cadastrar o lojista. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const validateUrl = (url) => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' +
        '((([a-zA-Z0-9\\-]+\\.)+[a-zA-Z]{2,})|' +
        '((\\d{1,3}\\.){3}\\d{1,3}))' +
        '(\\:\\d+)?(\\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?$',
      'i'
    );
    return !!urlPattern.test(url);
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData((prev) => ({
      ...prev,
      senha: password,
    }));
    showAppAlert('info', `Senha gerada: ${password} - Anote e repasse ao lojista`);
  };

  function maskCelular(value) {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  }

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <LoaderOverlay isLoading={loading} message="Salvando lojista..." />
      <div className="modal-content">
        <h2>Adicionar Lojista</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome:</label>
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
              className="input-style"
              required
            />
          </div>
          <div className="form-group">
            <label>Nome Fantasia:</label>
            <input
              type="text"
              name="nomeFantasia"
              value={formData.nomeFantasia}
              onChange={handleInputChange}
              className="input-style"
              required
            />
          </div>
          <div className="form-group">
            <label>E-mail:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="input-style"
              required
            />
          </div>
          <div className="form-group">
            <label>Senha:</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                name="senha"
                value={formData.senha}
                onChange={handleInputChange}
                className="input-style"
                style={{ flex: 1 }}
                required
              />
              <button
                type="button"
                onClick={generateRandomPassword}
                className="button-style"
                style={{ whiteSpace: 'nowrap' }}
              >
                Gerar
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Celular:</label>
            <input
              type="text"
              name="celular"
              value={formData.celular}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  celular: maskCelular(e.target.value),
                }))
              }
              className="input-style"
              required
            />
          </div>
          <div className="form-group">
            <label>URL Logotipo:</label>
            <input
              type="text"
              name="urlLogotipo"
              value={formData.urlLogotipo}
              onChange={handleInputChange}
              className="input-style"
            />
          </div>
          <div className="form-group">
            <label>Valor do Ponto:</label>
            <input
              type="number"
              step="1.00"
              name="valor_ponto"
              value={formData.valor_ponto}
              onChange={handleInputChange}
              className="input-style"
              required
            />
          </div>
          <div className="form-group">
            <label>Dias de Ausência:</label>
            <input
              type="number"
              name="dias_ausencia"
              value={formData.dias_ausencia}
              onChange={handleInputChange}
              className="input-style"
              required
            />
          </div>
          <div className="form-group">
            <label>Plano:</label>
            <select
              name="plano_id"
              value={formData.plano_id}
              onChange={handleInputChange}
              className="input-style"
              required
            >
              <option value="">Selecione um plano</option>
              {planos.map((plano) => (
                <option key={plano.id} value={plano.id}>
                  {plano.nome} - {plano.valor}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Place ID:</label>
            <input
              type="text"
              name="placeid"
              value={formData.placeid}
              onChange={handleInputChange}
              className="input-style"
            />
          </div>
          <div className="button-container">
            <button type="submit" className="button-style" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              className="button-style cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLojistaModal;